<script setup lang="ts">
import { computed } from "vue";
import Icon from "./Icon.vue";
import type { Format } from "../composables/composerFormat";
import { t } from "../i18n";

/**
 * WhatsApp Web's formatting bar (measured there): it floats 10 px above the text selected in the
 * composer, with bold, italic, strikethrough, inline code, numbered list, bulleted list and quote.
 * What the selection already has is lit. Pressing a button never takes the focus from the text.
 */
const props = defineProps<{
  /** Where the selection starts, on screen; `inline` shows the bar in place (the styleguide). */
  x?: number;
  y?: number;
  active: Format[];
  inline?: boolean;
}>();
const emit = defineEmits<{ apply: [format: Format] }>();

const mac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);
const key = (k: string) => (mac ? `⌘${k}` : `Ctrl+${k}`);
const BUTTONS: Array<{ format: Format; icon: string; shortcut?: string }> = [
  { format: "bold", icon: "wa-format-bold", shortcut: key("B") },
  { format: "italic", icon: "wa-format-italic", shortcut: key("I") },
  {
    format: "strike",
    icon: "wa-format-strike",
    shortcut: mac ? "⌘⇧X" : "Ctrl+Shift+X",
  },
  { format: "code", icon: "wa-format-code" },
  { format: "numbered", icon: "wa-format-numbered" },
  { format: "bulleted", icon: "wa-format-bulleted" },
  { format: "quote", icon: "wa-format-quote" },
];
const label = (b: (typeof BUTTONS)[number]) =>
  t(`composer.format.${b.format}` as never);

// 246 px wide, 42 high; kept 8 px inside the window.
const style = computed(() =>
  props.inline || props.x === undefined || props.y === undefined
    ? undefined
    : {
        left: `${Math.min(Math.max(props.x - 8, 8), window.innerWidth - 254)}px`,
        top: `${Math.max(props.y - 52, 8)}px`,
      },
);
</script>
<template>
  <Teleport to="body" :disabled="inline"
    ><div
      class="format-bar"
      :class="{ inline }"
      :style="style"
      role="toolbar"
      :aria-label="t('composer.format.bar')"
      @mousedown.prevent
    >
      <button
        v-for="b in BUTTONS"
        :key="b.format"
        v-tooltip="b.shortcut ? `${label(b)} · ${b.shortcut}` : label(b)"
        type="button"
        class="format-bar-button"
        :class="{ active: active.includes(b.format) }"
        :aria-label="label(b)"
        :aria-pressed="active.includes(b.format)"
        @click="emit('apply', b.format)"
      >
        <Icon :name="b.icon" :size="20" />
      </button></div
  ></Teleport>
</template>
